import { useCallback, useRef, useState } from 'react';
import { Box, ClickAwayListener, MenuItem, MenuList, Paper, Popper, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SHOW_LESS, SHOW_MORE } from 'common/constants/pageFilters';
import { useCurrencies } from 'components/Grid/GridFormatter/useCurrencies';

// The chip's brand green. Pinned as `WebkitTextFillColor` (not just `color`) on
// the badge text so it survives AI gradient-text contexts: an ancestor there sets
// `-webkit-text-fill-color: transparent` (an inherited property) to clip a gradient
// through its own text, which would otherwise blank the chip's ISO code and leave an
// empty outline. In normal contexts this is a no-op (fill already equals the color).
const MONEY_CHIP_COLOR = '#207255';

// Inline currency badge, styled per the figma reference at
// `currency-chip.jsx`. Shows the ISO code, optionally interactive: when an
// `onChange` is supplied, clicking opens the same dark "Currency Selections"
// menu used by the cap-table currency formatter (driven by `useCurrencies`).
//
// The chip is small enough to sit inside a single value-viewer row alongside
// the formatted amount — see `MoneyChipValueViewer` and
// `PreFxPopoutSpreadsheet`.

const ChipShell = styled(Box, {
  shouldForwardProp: prop => prop !== 'interactive',
})<{ interactive?: boolean }>(({ interactive }) => ({
  paddingLeft: 4,
  paddingRight: 4,
  paddingTop: 2,
  paddingBottom: 2,
  background: 'var(--Neutral-White, #ffffff)',
  borderRadius: 4,
  outline: `1px solid ${MONEY_CHIP_COLOR}`,
  outlineOffset: -1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: interactive ? 'pointer' : 'default',
  userSelect: 'none',
  // Tightening so the chip looks the same wherever it's mounted; figma spec.
  lineHeight: '14px',
  fontSize: 10,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  color: MONEY_CHIP_COLOR,
  WebkitTextFillColor: MONEY_CHIP_COLOR,
  // Don't let host wrappers stretch us into the row baseline; a small
  // baseline shim keeps the chip visually centered next to the amount text
  // even though the amount uses a larger font.
  alignSelf: 'center',
}));

const StyledMenuSeparator = styled(MenuItem)({
  borderBottom: '1px solid #EFEFEF',
});

const StyledShowMoreLess = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.875rem',
  color: (theme as any).palette?.grey?.[400] || '#9e9e9e',
}));

export interface MoneyChipProps {
  // Currency ISO code (e.g. "USD", "GBP"). Rendered verbatim.
  value: string;
  // When provided, the chip becomes interactive and a click opens the menu.
  // Omit for read-only display (host-cell decoration).
  onChange?: (next: string) => void;
  // Optional tooltip — useful when the host cell wants to explain context
  // (e.g. "Source currency before FX conversion"). Default: just the code.
  tooltip?: string;
  // Test id for popout / cell tests; forwarded onto the chip's outer element.
  testId?: string;
}

const MoneyChip: React.FC<MoneyChipProps> = ({
  value,
  onChange,
  tooltip,
  testId,
}) => {
  // Anchor strategy: keep a ref to the chip DOM node AND store an explicit
  // anchorEl in state. The ref is the stable identity across re-renders; the
  // state controls open/close. Earlier iterations of this component reached
  // for `event.currentTarget` from the click handler, which is correct
  // synchronously but drifts after re-renders, under rapid open/close cycles
  // (~2-3 in a row) MUI's Popover sometimes read a detached node and rendered
  // the menu in the page's top-left corner. Resolving the anchor from the
  // ref at open-time avoids that race entirely.
  const chipRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const interactive = Boolean(onChange);

  // Currency menu source: same hook the cap-table CurrencyFormatter uses, so
  // the order/symbols/show-more behavior matches what users already see when
  // changing display currency on a sheet. Hook is safe to call unconditionally
  // at the top level — cheap when interactive=false, just unused state.
  const { usedCurrencies, currencies, showMore, showLess } = useCurrencies();

  const isShowingPopular = currencies.length === usedCurrencies.length;

  const openMenu = useCallback(() => {
    if (!interactive) {
      return;
    }
    // Prefer the live DOM node from the ref; fall back to whatever React has
    // for `chipRef.current` rather than null so a Popper never anchors at the
    // viewport origin even in pathological cases.
    if (chipRef.current) {
      setAnchorEl(chipRef.current);
    }
  }, [interactive]);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // Stop propagation so the menu opening click doesn't also reach the
      // host data-grid (which would otherwise enter edit mode on the
      // underlying cell). Then anchor from the ref for stability.
      event.stopPropagation();
      openMenu();
    },
    [openMenu]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!interactive) {
        return;
      }
      // Enter / Space match the native <button> activation contract that the
      // chip is mimicking via role="button". Stop default so Space doesn't
      // scroll the spreadsheet, and so the host data-grid doesn't intercept
      // either key.
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        event.stopPropagation();
        openMenu();
      }
    },
    [interactive, openMenu]
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleListKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        event.preventDefault();
        handleClose();
      }
    },
    [handleClose]
  );

  const handlePick = useCallback(
    (code: string) => () => {
      onChange?.(code);
      setAnchorEl(null);
    },
    [onChange]
  );

  const chipBody = (
    <ChipShell
      ref={chipRef}
      interactive={interactive}
      onClick={interactive ? handleOpen : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      onMouseDown={interactive ? event => event.stopPropagation() : undefined}
      data-testid={testId}
      role={interactive ? 'button' : undefined}
      aria-label={interactive ? 'Change currency' : undefined}
      tabIndex={interactive ? 0 : undefined}>
      <Typography
        component="span"
        sx={{
          fontSize: 10,
          fontWeight: 400,
          lineHeight: '14px',
          // `&&` doubles this class's specificity so the chip's own color beats both
          // the inherited transparent fill AND the blanket `& *` transparent fill that
          // AI gradient-text wrappers apply to every descendant - keeping the ISO code
          // visible on AI-merge pending cells instead of blanking to an empty outline.
          '&&': { color: MONEY_CHIP_COLOR, WebkitTextFillColor: MONEY_CHIP_COLOR },
        }}>
        {value}
      </Typography>
    </ChipShell>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} placement="top">
          {chipBody}
        </Tooltip>
      ) : (
        chipBody
      )}
      {interactive && (
        <Popper
          anchorEl={anchorEl}
          open={open}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 2] } }]}>
          <ClickAwayListener onClickAway={handleClose}>
            <Paper elevation={8} sx={{ maxHeight: 300, overflow: 'auto' }}>
              <MenuList autoFocusItem={open} onKeyDown={handleListKeyDown}>
                {currencies.map(c => {
                  const isLastUsed = c.code === usedCurrencies[usedCurrencies.length - 1];
                  const ItemComponent = isLastUsed && isShowingPopular ? StyledMenuSeparator : MenuItem;
                  return (
                    <ItemComponent
                      key={c.code}
                      onClick={handlePick(c.code)}
                      selected={c.code === value}
                      data-testid={`money-chip-option-${c.code}`}>
                      {`${c.code} (${c.symbol})`}
                    </ItemComponent>
                  );
                })}
                <StyledShowMoreLess onClick={isShowingPopular ? showMore : showLess}>
                  {isShowingPopular ? SHOW_MORE : SHOW_LESS}
                </StyledShowMoreLess>
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default MoneyChip;
